import { useState } from "react";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { UseQueryResult, useQuery, useMutation } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ChangeOrderStatus from "../../components/Modals/ChangeOrderStatus";
import ReportDeliveryIssue from "../../components/Modals/ReportDeliveryIssue";
import Authorize from "../../components/Permission/Authorize";
import Table, { ITableHeader } from "../../components/Table";
import { Action, BasePermission } from "../../enums/permissions";
import { OrderService } from "../../services/order";
import { formatCurrency, formatRelativeDate } from "../../utils/formatter";
import { getFullName } from "../../utils/person";
import config from "../../config";

// Helper function to get status icon based on status name
const getStatusIcon = (statusName: string) => {
  const iconMap: { [key: string]: string } = {
    created: "ni ni-plus-circle",
    pending: "ni ni-clock",
    under_review: "ni ni-eye",
    in_progress: "ni ni-loader",
    shipped: "ni ni-truck",
    delivered: "ni ni-check-circle",
    canceled: "ni ni-cross-circle",
    rejected: "ni ni-cross-circle",
    paid: "ni ni-check-circle",
  };
  return iconMap[statusName] || "ni ni-circle";
};

// Helper function to get status color based on status name
const getStatusColor = (statusName: string) => {
  const colorMap: { [key: string]: string } = {
    created: "info",
    pending: "warning",
    under_review: "warning",
    in_progress: "primary",
    shipped: "info",
    delivered: "success",
    canceled: "danger",
    rejected: "danger",
    paid: "success",
  };
  return colorMap[statusName] || "secondary";
};

function hasBillingAddressInfo(address: unknown): boolean {
  if (!address || typeof address !== "object") {
    return false;
  }
  const record = address as Record<string, unknown>;
  if (Object.keys(record).length === 0) {
    return false;
  }
  const fields = [
    "address",
    "neighborhood",
    "municipality",
    "province",
    "reference",
  ];
  return fields.some((key) => {
    const value = record[key];
    return typeof value === "string" && value.trim().length > 0;
  });
}

function OrderDetails() {
  const { docsDomain } = config;
  const { id: orderId } = useParams();
  const intl = useIntl();
  const orderDetailsQuery = useQuery(["orderDetails", orderId], () =>
    OrderService.getById(String(orderId)),
  );

  const handlePrint = async () => {
    try {
      // Get the print HTML from the backend
      const printHtml = await OrderService.getPrintHtml(String(orderId));

      // Create a print-friendly version
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        // Fallback to simple print
        window.print();
        return;
      }

      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error("Print error:", error);
      // Fallback to simple print
      window.print();
    }
  };

  const handleClose = () => {
    orderDetailsQuery.refetch();
    setShow(false);
  };
  const handleShow = () => setShow(true);

  // Helper function to check if status allows changes
  const canChangeStatus = (statusName: string) => {
    return statusName !== "delivered" && statusName !== "canceled";
  };

  const [show, setShow] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);

  const showChangeStatus =
    orderDetailsQuery.isLoading ||
    (orderDetailsQuery.isSuccess &&
      canChangeStatus(orderDetailsQuery.data?.status?.name));

  return (
    <div className="container-fluid">
      <div className="nk-content-inner">
        <div className="nk-content-body">
          {orderDetailsQuery.isSuccess &&
            canChangeStatus(orderDetailsQuery.data?.status?.name) && (
              <ChangeOrderStatus
                order={orderDetailsQuery.data}
                show={show}
                onHide={handleClose}
              />
            )}
          {orderDetailsQuery.isSuccess && (
            <ReportDeliveryIssue
              show={showReportIssue}
              onHide={() => setShowReportIssue(false)}
              orderId={orderDetailsQuery.data?.id}
              orderCode={orderDetailsQuery.data?.code}
            />
          )}
          <div className="nk-block-head nk-block-head-sm">
            <div className="nk-block-between">
              <div className="nk-block-head-content">
                <h3 className="nk-block-title page-title d-flex align-items-center">
                  #{orderDetailsQuery.data?.code}
                  <a
                    href={`${docsDomain}/docs/guia-de-uso/negocios/pedidos`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-link btn-sm d-flex align-items-center"
                    title={intl.formatMessage({ id: "View documentation" })}
                  >
                    <em className="icon ni ni-help me-1"></em>
                    <FormattedMessage id="Help" />
                  </a>
                </h3>
                <div className="nk-block-des text-soft">
                  <FormattedMessage id="Realized by" />{" "}
                  <Link
                    to={`/customers/${orderDetailsQuery.data?.customer?.id}`}
                    className="text-primary text-decoration-none fw-medium"
                  >
                    {orderDetailsQuery.data?.customer &&
                      getFullName(orderDetailsQuery.data?.customer.user)}
                  </Link>
                  {" • "}
                  <span className="text-muted">
                    {orderDetailsQuery.data?.createdAt &&
                      formatRelativeDate(
                        new Date(orderDetailsQuery.data.createdAt),
                      )}
                  </span>
                </div>
              </div>
              <div className="nk-block-head-content">
                <ul className="nk-block-tools g-3">
                  <li className="nk-block-tools-opt">
                    <Button variant="light" onClick={handlePrint}>
                      <em className="icon ni ni-printer-fill me-1" />
                      <FormattedMessage id="Print" />
                    </Button>
                  </li>
                  <li className="nk-block-tools-opt">
                    {showChangeStatus ? (
                      <Authorize
                        actions={[Action.UPDATE]}
                        subjects={[BasePermission.Order]}
                      >
                        <Button onClick={handleShow}>
                          <FormattedMessage id="Change Status" />
                        </Button>
                      </Authorize>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() => setShowReportIssue(true)}
                      >
                        <em className="icon ni ni-alert me-1" />{" "}
                        <FormattedMessage id="report a problem" />
                      </Button>
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="nk-block">
            <div className="row">
              <div className="col-xxl-4 col-md-4">
                <OrderHistoryChanges {...orderDetailsQuery} />
              </div>
              <div className="col-xxl-8 col-md-8">
                <div className="h-100">
                  <OrderDeliveryInfo {...orderDetailsQuery} />
                  <PaymentInformation data={orderDetailsQuery.data} isSuccess={orderDetailsQuery.isSuccess} />
                  <br /><hr /><br />
                  <OrderItemList {...orderDetailsQuery} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderHistoryChanges({ data, isLoading }: UseQueryResult<any, any>) {
  const { history = [] } = data || ({} as any);

  const renderTooltip = (ch: any) => (
    <Tooltip id={`tooltip-${ch.id}`}>
      <div className="text-start">
        <strong>{getFullName(ch.modifiedBy)}</strong> <br />
        <small>
          {ch.modifiedBy?.email || <FormattedMessage id="System Admin" />}
        </small>
        <br />
        <small>{formatRelativeDate(new Date(ch.createdAt))}</small>
      </div>
    </Tooltip>
  );

  return (
    <div className="card h-100">
      <div className="card-inner">
        <div className="card-title-group mb-2">
          <div className="card-title d-flex justify-content-between w-100">
            <h6 className="title">
              <FormattedMessage id="change history" />
            </h6>
            {data?.status && (
              <span
                className={`badge badge-sm badge-dot has-bg bg-${getStatusColor(data.status.name)} ms-2`}
              >
                {data.status.description}
              </span>
            )}
          </div>
        </div>
        <hr />

        <ul
          className="timeline-list"
          style={{
            paddingLeft: "10px",
          }}
        >
          {isLoading &&
            Array(3)
              .fill({})
              .map((_, i) => (
                <div className="loading-skeleton" key={i}>
                  <div className="skeleton-row">
                    <div className="block">
                      <FormattedMessage id="Loading" />
                    </div>{" "}
                  </div>
                </div>
              ))}

          {history.length > 0 ? (
            history.map((ch: any, index: number) => {
              const statusName = ch.status?.name || "unknown";
              const statusColor = getStatusColor(statusName);
              const statusIcon = getStatusIcon(statusName);

              return (
                <li className="timeline-item" key={ch.id || index}>
                  <OverlayTrigger
                    key={ch.id || index}
                    placement="right"
                    overlay={renderTooltip(ch)}
                  >
                    <div
                      className={`timeline-status bg-${statusColor} text-white d-flex align-items-center justify-content-center`}
                      style={{
                        position: "relative",
                        left: "-10px",
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        minWidth: "32px",
                        marginRight: "12px",
                      }}
                    >
                      <em className={statusIcon} style={{ fontSize: "14px" }} />
                    </div>
                  </OverlayTrigger>
                  <div
                    className="timeline-data"
                    style={{
                      flex: 1,
                      alignSelf: "center",
                    }}
                  >
                    <h6
                      className="timeline-title"
                      style={{
                        marginBottom: "0",
                      }}
                    >
                      {ch.status?.description || statusName}
                    </h6>
                    <div className="timeline-des">
                      <small className="text-muted">{ch.comment}</small>
                    </div>
                  </div>
                  <div className="timeline-date">
                    <small> {formatRelativeDate(new Date(ch.createdAt))}</small>
                  </div>
                </li>
              );
            })
          ) : (
            <div className="py-4">
              <h5 className="text-soft">
                <FormattedMessage id="No data to display" />{" "}
              </h5>
            </div>
          )}
        </ul>
      </div>
    </div>
  );
}

function OrderDeliveryInfo({
  isLoading,
  data,
  refetch,
  isSuccess,
}: UseQueryResult<any, unknown> & { refetch: () => void; isSuccess: boolean }) {
  const navigate = useNavigate();
  const confirmPaymentMutation = useMutation(
    (transactionId: string) => OrderService.confirmPayment(transactionId),
    {
      onSuccess: (data) => {
        toast.success(data.message || "Pago confirmado exitosamente");
        refetch();
      },
      onError: (error: any) => {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error al confirmar el pago";
        toast.error(errorMessage);
      },
    },
  );

  const handleConfirmPayment = () => {
    if (data?.transaction?.id) {
      confirmPaymentMutation.mutate(data.transaction.id);
    }
  };

  // Helper function to check if order needs payment
  const needsPayment = (statusName: string) => {
    return statusName === "created" || statusName === "pending";
  };

  // Helper function to check if order has preauthorized transaction
  const hasPreauthorizedTransaction = (orderData: any) => {
    return orderData?.transaction?.status === "preauthorized";
  };

  // Helper function to check if transaction is completed
  const hasCompletedTransaction = (orderData: any) => {
    return orderData?.transaction?.status === "completed";
  };

  return (
    <Row className="mb-4">
      <Col>
        <div className="card slim-table-loading">
          <div className="card-inner">
            <div className="card-title-group">
              <div className="card-title">
                <h6 className="title">
                  <FormattedMessage id="Shipping info" />
                </h6>
              </div>
            </div>
            <hr />
            {isLoading &&
              Array(2)
                .fill({})
                .map((_, i) => (
                  <div className="loading-skeleton" key={i}>
                    <div className="skeleton-row">
                      <div className="block">
                        <FormattedMessage id="Loading" />
                      </div>{" "}
                    </div>
                  </div>
                ))}
            {!isLoading && data?.address ? (
              <div className="mt-3">
                <b>
                  {data?.address?.address} en {data?.address?.neighborhood}
                </b>
                <p>
                  {data?.address?.province}, {data?.address?.municipality}
                </p>

                <p className="mt-4 ff-italic text-soft">
                  {data?.address?.reference ? (
                    <>
                      <b className="mr-2">Referencia:</b>{" "}
                      {data?.address?.reference}
                    </>
                  ) : (
                    <>
                      <FormattedMessage id="No reference" />
                    </>
                  )}
                </p>
              </div>
            ) : !isLoading && data?.shippingAddress ? (
              <div className="mt-3">{data?.shippingAddress}</div>
            ) : !isLoading ? (
              <div className="py-4">
                <h5 className="text-soft">
                  <FormattedMessage id="No data to display" />{" "}
                </h5>
              </div>
            ) : null}
          </div>
          <div
            className="card-footer d-flex"
            style={{ justifyContent: "space-between" }}
          >
            {isSuccess && data?.estimatedDeliveryDate ? (
              <span
                style={{ paddingTop: "2px", paddingBottom: "2px" }}
                className="badge remove-before badge-dot badge-sm has-bg bg-secondary align-items-center"
              >
                <em className="icon ni ni-calendar me-1" />
                <FormattedMessage id="Delivery" />{" "}
                {formatRelativeDate(new Date(data.estimatedDeliveryDate))}
              </span>
            ) : null}
            <Authorize
              actions={[Action.READ]}
              subjects={[BasePermission.Customer]}
            >
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate(`/customers/${data?.customer?.id}`)}
              >
                <em className="icon ni ni-arrow-right me-1" />
                <FormattedMessage id="Go to address" />
              </Button>
            </Authorize>
          </div>
        </div>
      </Col>
      <Col>
        <div className="card slim-table-loading h-100">
          <div className="card-inner h-100">
            <div className="card-title-group">
              <div className="card-title">
                <h6 className="title">
                  <FormattedMessage id="Billing info" />
                </h6>
              </div>

              <div className="card-tools">
                {/* {isSuccess && hasCompletedTransaction(data) ? (
                  <span
                    style={{ paddingTop: "2px", paddingBottom: "2px" }}
                    className="badge remove-before badge-dot badge-sm has-bg bg-success d-flex align-items-center"
                  >
                    <em className="icon ni ni-check-circle me-1" />
                    <FormattedMessage id="Charged" />
                  </span>
                ) : (
                  <span
                    style={{ paddingTop: "2px", paddingBottom: "2px" }}
                    className="badge remove-before badge-dot badge-sm has-bg bg-danger d-flex align-items-center"
                  >
                    <em className="icon ni ni-cross-circle me-1" />
                    <FormattedMessage id="Not Charged" />
                  </span>
                )} */}
              </div>
            </div>
            <hr />

            {isLoading &&
              Array(2)
                .fill({})
                .map((_, i) => (
                  <div className="loading-skeleton" key={i}>
                    <div className="skeleton-row">
                      <div className="block">
                        <FormattedMessage id="Loading" />
                      </div>{" "}
                    </div>
                  </div>
                ))}

            {!isLoading &&
            data?.billing &&
            Object.keys(data?.billing).length > 0 ? (
              <>
                <div className="mt-3">
                  Dirección de pago:
                  <p>
                    {hasBillingAddressInfo(data?.billing?.address) ? (
                      <>
                        {data.billing.address.address},{" "}
                        {data.billing.address.neighborhood},{" "}
                        {data.billing.address.municipality},{" "}
                        {data.billing.address.province}.{" "}
                        {data.billing.address.reference
                          ? `(${data.billing.address.reference})`
                          : ""}
                      </>
                    ) : (
                      <FormattedMessage id="Use the same as shipping" />
                    )}
                  </p>
                </div>
                {data?.billing.taxInfo &&
                  data?.billing.taxInfo.name &&
                  data?.billing.taxInfo.ide && (
                    <p className="mt-4 ff-italic text-soft">
                      <b className="mr-2">Facturar a nombre de:</b>{" "}
                      {data.billing.taxInfo.name} - Identificación:{" "}
                      {data.billing.taxInfo.ide}
                    </p>
                  )}
              </>
            ) : !isLoading ? (
              <div className="py-3">
                <p className="text-soft">
                  <FormattedMessage id="No data to display" />{" "}
                </p>
              </div>
            ) : null}
          </div>
          {!config.autoChargeEnabled ? <div className="card-footer text-end">
            {isSuccess && hasPreauthorizedTransaction(data) ? (
              <Authorize
                actions={[Action.UPDATE]}
                subjects={[BasePermission.Order]}
              >
                <Button
                  size="sm"
                  variant="success"
                  onClick={handleConfirmPayment}
                  disabled={confirmPaymentMutation.isLoading}
                >
                  <em className="icon ni ni-wallet-fill me-1 " />
                  {confirmPaymentMutation.isLoading ? (
                    <FormattedMessage id="Processing..." />
                  ) : (
                    <FormattedMessage id="Charge" />
                  )}
                </Button>
              </Authorize>
            ) : (
              <Authorize
                actions={[Action.UPDATE]}
                subjects={[BasePermission.Order]}
              >
                <Button size="sm" variant="success" disabled>
                  <FormattedMessage id="Paid" />
                </Button>
              </Authorize>
            )}
          </div> : null}
        </div>
      </Col>
    </Row>
  );
}

function PaymentInformation({ isSuccess, data }: { isSuccess: boolean; data: any }) {
  if (!isSuccess || !data?.transaction) {
    return null;
  }

  const getTransactionStatusKey = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      completed: "Completed",
      preauthorized: "Preauthorized",
      failed: "Failed",
      cancelled: "Cancelled",
      pending: "Pending",
      processing: "Processing",
    };
    return statusMap[status] || status;
  };

  const getStatusBadgeClass = (status: string): string => {
    const statusClasses: { [key: string]: string } = {
      completed: "bg-success",
      preauthorized: "bg-warning",
      failed: "bg-danger",
      cancelled: "bg-gray",
      pending: "bg-info",
      processing: "bg-info",
    };
    return statusClasses[status] || "bg-info";
  };

  return (
    <Row className="gy-4 mt-3">
      <Col>
        <div className="card">
          <div className="card-inner">
            <div className="card-title-group mb-3">
              <div className="card-title">
                <h6 className="title">
                  <FormattedMessage id="Transaction Information" />
                </h6>
              </div>
              <div className="card-tools">
                <span
                  className={`badge badge-sm badge-dim ${getStatusBadgeClass(
                    data.transaction.status
                  )}`}
                >
                  <FormattedMessage
                    id={getTransactionStatusKey(data.transaction.status)}
                  />
                </span>
              </div>
            </div>
            <hr />
            <div className="row gy-3">
              <div className="col-md-6">
                <span className="text-soft">
                  <FormattedMessage id="Transaction ID" />:
                </span>
                <div className="fw-bold">{data.transaction.transactionId?.toUpperCase()}</div>
              </div>
              {/* {data.transaction.gatewayTransactionId && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="Gateway Transaction ID" />:
                  </span>
                  <div className="fw-bold">
                    {data.transaction.gatewayTransactionId}
                  </div>
                </div>
              )} */}
              {data.transaction.preAuthDetails?.retrievalReferenceNumber && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="Reference Number" />:
                  </span>
                  <div className="fw-bold">
                    {data.transaction.preAuthDetails.retrievalReferenceNumber}
                  </div>
                </div>
              )}
              {data.transaction.preAuthDetails?.authorizationCode && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="Authorization Code" />:
                  </span>
                  <div className="fw-bold">
                    {data.transaction.preAuthDetails.authorizationCode}
                  </div>
                </div>
              )}
              {data.transaction.preAuthDetails?.externalTransactionId && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="External Transaction ID" />:
                  </span>
                  <div className="fw-bold">
                    {data.transaction.preAuthDetails.externalTransactionId}
                  </div>
                </div>
              )}
              {data.transaction.preAuthDetails?.creditCardMasked && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="Card Number" />:
                  </span>
                  <div className="fw-bold">
                     {data.transaction.preAuthDetails.creditCardMasked} 
                  </div>
                </div>
              )}
              {data.transaction.amount && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="Amount" />:
                  </span>
                  <div className="fw-bold">
                    {data.transaction.currency || "RD$"}{" "}
                    {data.transaction.amount.toFixed(2)}
                  </div>
                </div>
              )}
              {data.transaction.processedAt && (
                <div className="col-md-6">
                  <span className="text-soft">
                    <FormattedMessage id="Processed At" />:
                  </span>
                  <div className="fw-bold">
                    {new Date(data.transaction.processedAt).toLocaleString()}
                  </div>
                </div>
              )}
              {data.transaction.failureReason && (
                <div className="col-12">
                  <span className="text-soft">
                    <FormattedMessage id="Failure Reason" />:
                  </span>
                  <div className="text-danger fw-bold">
                    {data.transaction.failureReason}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}

function OrderItemList({ isLoading, data }: UseQueryResult<any, unknown>) {
  const intl = useIntl();
  const headers: ITableHeader[] = [
    {
      path: ["id"],
      name: "ID",
      transform: (value, obj) => (
        <span className="tb-lead">
          <a href="#">#{obj.product?.code}</a>
        </span>
      ),
    },
    {
      path: ["productName"],
      name: intl.formatMessage({ id: "productNameHeader" }),
      className: "lg-table-col",
      transform: (value, obj) => {
        return (
          <span className="tb-product">
            <img
              src={
                obj.product?.mediaAssets[0]?.url ||
                obj.product?.mediaAssets[0]?.thumbnail
              }
              height={50}
              alt=""
              className="thumb"
            />
            <span className="title">{value}</span>
          </span>
        );
      },
    },
    {
      path: ["quantity"],
      name: intl.formatMessage({ id: "amount" }),
    },
    {
      path: ["price"],
      name: intl.formatMessage({ id: "priceHeader" }),
      transform: formatCurrency,
    },
    {
      path: ["price"],
      name: intl.formatMessage({ id: "Total" }),
      transform: (_, obj) => {
        const value = obj.price * obj.quantity;
        return formatCurrency(value);
      },
    },
    /*  {
      path: ["options"], // no id
      name: "",
      type: "tools",
      transform: (v, entity) => (
        <Dropdown
          key={entity.id}
          icon
          trigger
          text={<em className="icon ni ni-more-h" />}
        >
          <DropdownMenu options={options} entity={entity} />
        </Dropdown>
      ),
    }, */
  ];

  return (
    <div className="card slim-table-loading">
      <div className="card-inner">
        <div className="card-title-group">
          <div className="card-title">
            <h6 className="title">
              <FormattedMessage id="order items" />
            </h6>
          </div>
        </div>
        <hr />
      </div>
      <Table
        className="slim-borders"
        loading={isLoading}
        headers={headers}
        data={data?.items}
      />
      <div className="card-inner text-right">
        <div className="row">
          {isLoading ? (
            <>
              <div className="col-md-6"></div>
              <div className="col-md-6">
                {Array(2)
                  .fill({})
                  .map((_, i) => (
                    <div className="loading-skeleton" key={i}>
                      <div className="skeleton-row">
                        <div className="block">
                          <FormattedMessage id="Loading" />
                        </div>{" "}
                      </div>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <div className="col">
              <div className="row">
                <div className="col text-right px-0">
                  <h6>
                    <FormattedMessage id="Total" />:
                  </h6>
                </div>
                <div className="col-sm-3 pl-0">
                  {" "}
                  {formatCurrency(data?.subTotal)}
                </div>
              </div>

              <div className="row">
                <div className="col text-right px-0">Impuestos:</div>
                <div className="col-sm-3 pl-0">
                  {" "}
                  {formatCurrency(data?.tax)}
                </div>
              </div>
              <div className="row mt-1">
                <div className="col text-right px-0">
                  Envió{" "}
                  <span className="text-muted">
                    ({data?.delivery?.description})
                  </span>
                  :
                </div>
                <div className="col-sm-3 pl-0">
                  {formatCurrency(data?.delivery?.price)}
                </div>
              </div>

              <div className="row">
                <div
                  className="col px-0"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <h6
                    style={{ marginRight: "0.5em", fontWeight: "normal" }}
                    className="d-inline-block"
                  >
                    Total Gral:
                  </h6>
                </div>
                <div className="col-sm-3 pl-0">
                  <h4 className="mt-3 d-inline-block">
                    {formatCurrency(data?.total)}
                  </h4>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;
